/**
 * Frontend validation-error translator — STANDALONE.
 *
 * Drop this file into the frontend repo as-is. It has NO dependency on the
 * backend or the monorepo; the two interfaces below mirror the backend contract
 * emitted by `@mercury/mercury-odm`'s `validationErrorBody()` (see
 * packages/mercury-odm/src/ValidationError.ts). If the backend `FieldIssue`
 * shape changes, update the mirror here to match.
 *
 * Purpose: a write to an ODM-backed endpoint (e.g. POST/PATCH
 * /line-item-definitions) that violates the collection's `$jsonSchema` / `@Rule`
 * validator now returns HTTP 400 with this body instead of an opaque 500:
 *
 *   {
 *     "name": "validation_error",
 *     "type": "validation_error",
 *     "message": "'fixedQuantity' must be of type number (received string); 'name' is required",
 *     "details": [
 *       { "path": "fixedQuantity", "rule": "bsonType", "message": "...", "expected": {...}, "actual": "abc" },
 *       { "path": "name",          "rule": "required", "message": "'name' is required" }
 *     ]
 *   }
 *
 * `mapValidationToPayload(body, payload)` resolves each issue's `path` against
 * the exact object you submitted, so a form can highlight the offending control
 * AND show the value the user actually sent.
 */

/* ----------------------------- backend contract ---------------------------- */

/** Mirror of the backend `FieldIssue` (mercury-odm/src/ValidationError.ts). */
export interface FieldIssue {
    /** Dot/array path to the field, e.g. "fixedQuantity", "items.0.name". "" = whole-document rule. */
    path: string;
    /** Failed keyword: "bsonType" | "enum" | "required" | "additionalProperties" | "minimum" | … | "rule". */
    rule: string;
    /** Human-readable, display-ready message. */
    message: string;
    /** What the schema required (keyword `specifiedAs`), when known. */
    expected?: unknown;
    /** The value MongoDB considered, when known. */
    actual?: unknown;
}

/** Mirror of the backend `ValidationErrorBody`. */
export interface ValidationErrorBody {
    name: "validation_error";
    type: "validation_error";
    message: string;
    details: FieldIssue[];
}

/* ------------------------------- public types ------------------------------ */

export interface ResolvedIssue extends FieldIssue {
    /** `path` split into addressable segments (numeric segments become numbers). */
    segments: Array<string | number>;
    /**
     * The value found at `path` in the SUBMITTED payload. `undefined` when the
     * field was missing (e.g. a `required` failure) or removed by the server.
     * Prefer this over `issue.actual` for display — it is the literal thing the
     * user sent, before any BSON coercion.
     */
    submittedValue: unknown;
    /** True when `path` actually resolved to something in the payload. */
    present: boolean;
}

export interface MappedValidation {
    /** Every issue, resolved against the payload, in server order. */
    issues: ResolvedIssue[];
    /** Issues grouped by `path` — direct lookup for a form control. */
    byPath: Record<string, ResolvedIssue[]>;
    /** Flat, display-ready summary lines (the `message`s). */
    messages: string[];
    /** Whole-document issues (empty `path`, typically `@Rule` violations). */
    formLevel: ResolvedIssue[];
}

/* -------------------------------- functions -------------------------------- */

/** Type guard: is this response body a translated validation error? */
export function isValidationError(body: unknown): body is ValidationErrorBody {
    return (
        !!body &&
        typeof body === "object" &&
        (body as any).type === "validation_error" &&
        Array.isArray((body as any).details)
    );
}

/**
 * Split a backend path ("a.b.0.c" or "a[0].b") into segments, coercing
 * array-index segments to numbers so they index arrays correctly.
 */
export function parsePath(path: string): Array<string | number> {
    if (!path) return [];
    return path
        .replace(/\[(\d+)\]/g, ".$1") // a[0].b -> a.0.b
        .split(".")
        .filter((s) => s.length > 0)
        .map((s) => (/^\d+$/.test(s) ? Number(s) : s));
}

/** Safely read `segments` out of `payload`; returns [value, present]. */
function getByPath(payload: unknown, segments: Array<string | number>): [unknown, boolean] {
    let cur: any = payload;
    for (const seg of segments) {
        if (cur == null || typeof cur !== "object") return [undefined, false];
        if (!(seg in cur)) return [undefined, false];
        cur = cur[seg];
    }
    return [cur, true];
}

/**
 * Translate a 400 validation body back against the payload that was sent.
 *
 * @param body    The parsed response JSON (must satisfy `isValidationError`).
 * @param payload The exact object you POST/PATCHed.
 *
 * @example
 *   const res = await fetch("/line-item-definitions", { method: "POST", body: JSON.stringify(payload) });
 *   if (res.status === 400) {
 *     const body = await res.json();
 *     if (isValidationError(body)) {
 *       const { byPath, messages } = mapValidationToPayload(body, payload);
 *       setFieldError("fixedQuantity", byPath["fixedQuantity"]?.[0]?.message);
 *       toast(messages.join("\n"));
 *     }
 *   }
 */
export function mapValidationToPayload(body: ValidationErrorBody, payload: unknown): MappedValidation {
    const issues: ResolvedIssue[] = body.details.map((issue) => {
        const segments = parsePath(issue.path);
        const [submittedValue, present] = getByPath(payload, segments);
        return { ...issue, segments, submittedValue, present };
    });

    const byPath: Record<string, ResolvedIssue[]> = {};
    for (const issue of issues) {
        (byPath[issue.path] ??= []).push(issue);
    }

    return {
        issues,
        byPath,
        messages: issues.map((i) => i.message),
        formLevel: issues.filter((i) => i.path === ""),
    };
}

/* ------------------------------ websocket acks ----------------------------- */

/**
 * WebSocket transport for the SAME contract. A socket callback has no HTTP
 * status, so write handlers ack with a discriminated envelope (mirror of the
 * backend `WsAck<T>` in apps/invoicing/src/types):
 *   { ok: true,  data }
 *   { ok: false, error: ValidationErrorBody | { name; message; type; details: null } }
 */
export type WsAck<T> =
    | { ok: true; data: T }
    | { ok: false; error: ValidationErrorBody | { name: string; message: string; type: string; details: null } };

/** True when a WS ack is a *validation* failure (vs. a generic exception). */
export function isValidationAck<T>(ack: WsAck<T>): ack is { ok: false; error: ValidationErrorBody } {
    return ack.ok === false && isValidationError(ack.error);
}

/**
 * One-call translator for a WS ack — the socket analogue of
 * `mapValidationToPayload`. Returns the mapped validation when the ack is a
 * validation failure, otherwise `null` (success, or a generic exception you'd
 * show as a toast).
 *
 * @example
 *   socket.emit("createLineItemDefinition", { body: payload }, (ack) => {
 *     const mapped = mapAckToPayload(ack, payload);
 *     if (mapped) { for (const i of mapped.issues) setFieldError(i.path, i.message); return; }
 *     if (!ack.ok) { toast(ack.error.message); return; }   // generic exception
 *     onSaved(ack.data);                                    // success
 *   });
 */
export function mapAckToPayload<T>(ack: WsAck<T>, payload: unknown): MappedValidation | null {
    return isValidationAck(ack) ? mapValidationToPayload(ack.error, payload) : null;
}
